import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useBoardStore } from '../store/useBoardStore';
import { readBoardGoal } from '../utils/boardGoalFields';
import { useGeminiLanguage } from '../hooks/useGeminiLanguage';
import { getCanvasMessages } from '../utils/canvasI18n';

/** Board goal input + Run. Saving it regenerates the steps (BoardCurrentStep shows the current one). */
const BoardGoalEditor = ({ onSaved = null, className = '' } = {}) => {
  const saveDescription = useBoardStore(state => state.saveDescription);
  const initialDescription = useBoardStore(state => readBoardGoal(state.board));
  const canEdit = useBoardStore(state => state.isOwner);
  const { geminiLanguage } = useGeminiLanguage();
  const messages = getCanvasMessages(geminiLanguage);
  const [description, setDescription] = useState(initialDescription);
  // Tracks the last store value we synced so we can tell whether the user has
  // drifted from it. If they have, an incoming store change (e.g. the API
  // response arriving mid-edit) must not overwrite their unsaved text.
  const lastSyncedRef = useRef(initialDescription || '');

  useEffect(() => {
    const next = initialDescription || '';
    setDescription(prev => {
      if (String(prev) === lastSyncedRef.current) {
        // User hasn't changed the field since the last store sync — safe to follow the store.
        lastSyncedRef.current = next;
        return next;
      }
      // User has unsaved edits in flight — keep their text, just update the ref
      // so the next store change is evaluated against the latest server value.
      lastSyncedRef.current = next;
      return prev;
    });
  }, [initialDescription]);

  // Edited text stays pending — commit (and run AI) only via the Run button.
  const isDirty = String(initialDescription || '') !== String(description || '');

  const handleRun = useCallback(() => {
    if (String(initialDescription || '') === String(description || '')) return;
    void saveDescription(description).then(ok => {
      if (ok && typeof onSaved === 'function') onSaved();
    });
  }, [description, initialDescription, onSaved, saveDescription]);

  if (!canEdit && !description.trim()) {
    return null;
  }

  return (
    <Box className={['board-description', className].filter(Boolean).join(' ')}>
      {canEdit ? (
        <>
          <TextField
            className="board-description-input"
            size="small"
            fullWidth
            multiline
            maxRows={2}
            placeholder={messages.boardGoalPlaceholder}
            value={description}
            onChange={e => setDescription(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (isDirty) {
                  handleRun();
                } else {
                  e.target.blur();
                }
              }
            }}
            inputProps={{ maxLength: 500, 'aria-label': messages.boardGoalAria }}
          />
          {isDirty ? (
            <Button
              size="small"
              variant="contained"
              className="board-description-run-btn"
              onClick={handleRun}
            >
              Run
            </Button>
          ) : null}
          <Tooltip title={messages.boardGoalTooltip} arrow placement="top">
            <IconButton
              size="small"
              className="board-description-info"
              aria-label={messages.aboutBoardGoal}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" className="board-description-readonly">
          {description}
        </Typography>
      )}
    </Box>
  );
};

export default BoardGoalEditor;
